/**
 * Sprint TC-B.2 — A single "application" card for the candidate dashboard.
 *
 * Renders the role/company context that the candidate is being evaluated for
 * plus the embedded assessments and the explicit next action. Pure
 * presentational; consumes the shape returned by `GET /api/me/dashboard`.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeAssignmentTitle } from '@/lib/i18nTitles';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Briefcase, Building2, MapPin, CalendarClock, Users, ArrowRight,
  CheckCircle2, Clock, ShieldCheck, ChevronDown, ChevronUp, Inbox,
} from 'lucide-react';
import { getUiLocale } from '@/lib/dates';

const STATUS_TONE = {
  invited:     'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  submitted:   'bg-green-100 text-green-700 border-green-200',
  closed:      'bg-muted text-muted-foreground border-muted',
};

const FINAL_OUTCOME_TONE = {
  selected:     'bg-green-100 text-green-700 border-green-200',
  not_selected: 'bg-slate-100 text-slate-700 border-slate-200',
  on_hold:      'bg-amber-100 text-amber-700 border-amber-200',
  cancelled:    'bg-red-100 text-red-700 border-red-200',
};

const FINAL_OUTCOME_LABEL = {
  selected:     { es: 'Seleccionado/a', en: 'Selected' },
  not_selected: { es: 'No seleccionado/a', en: 'Not selected' },
  on_hold:      { es: 'En pausa', en: 'On hold' },
  cancelled:    { es: 'Proceso cancelado', en: 'Process cancelled' },
};

const fmt = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(getUiLocale()); } catch { return iso; }
};

const ASSIGNMENT_TONE = {
  requested:   'bg-info/10 text-info border-info/30',
  in_progress: 'bg-warning/10 text-warning border-warning/30',
  submitted:   'bg-success/10 text-success border-success/30',
};

export default function ApplicationCard({ app }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isES = (i18n.language || '').startsWith('es');
  const [expanded, setExpanded] = useState(app.assignments?.length === 1);

  const statusLabel = t(`recruitment.candidate.status.${app.application_status}`, {
    defaultValue: app.application_status.replace(/_/g, ' '),
  });
  const tone = STATUS_TONE[app.application_status] || STATUS_TONE.invited;

  const handleNext = () => {
    if (app.next_action?.route) {
      navigate(app.next_action.route);
    }
  };

  const nextLabel = (() => {
    const k = app.next_action?.kind;
    if (k === 'continue_assessment') return isES ? 'Continuar evaluación' : 'Continue assessment';
    if (k === 'start_assessment')    return isES ? 'Iniciar evaluación'   : 'Start assessment';
    if (k === 'awaiting_hr_outcome') return isES ? 'Esperando respuesta de RR.HH.' : 'Awaiting HR outcome';
    if (k === 'process_concluded')   return isES ? 'Proceso concluido' : 'Process concluded';
    return null;
  })();

  return (
    <Card
      className="p-6 shadow-card hover:shadow-elegant transition-smooth border-primary/10"
      data-testid={`application-card-${app.process_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-primary font-semibold">
            <Briefcase className="h-3 w-3" />
            {isES ? 'Estás siendo evaluado/a para' : 'You are being evaluated for'}
          </div>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1 truncate" data-testid="application-position">
            {app.position_title || '—'}
          </h3>
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground flex-wrap">
            <Building2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{app.company_name || '—'}</span>
            {app.department && (
              <>
                <span className="text-muted-foreground/50">·</span>
                <span className="truncate">{app.department}</span>
              </>
            )}
          </div>
        </div>
        <Badge className={`${tone} border`} data-testid="application-status">
          {statusLabel}
        </Badge>
      </div>

      {/* Quick facts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
        {app.work_modality && (
          <Fact icon={MapPin} label={isES ? 'Modalidad' : 'Modality'} value={app.work_modality} />
        )}
        {app.closing_date && (
          <Fact icon={CalendarClock} label={isES ? 'Fecha límite' : 'Closing date'} value={fmt(app.closing_date)} />
        )}
        {app.number_of_openings != null && (
          <Fact icon={Users} label={isES ? 'Vacantes' : 'Openings'} value={String(app.number_of_openings)} />
        )}
        {app.assessments_total > 0 && (
          <Fact
            icon={CheckCircle2}
            label={isES ? 'Evaluaciones' : 'Assessments'}
            value={`${app.assessments_completed}/${app.assessments_total}`}
          />
        )}
      </div>

      {/* Overall progress */}
      {app.assessments_total > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground">
              {isES ? 'Progreso general' : 'Overall progress'}
            </span>
            <span className="tabular-nums font-medium">{app.average_progress}%</span>
          </div>
          <Progress value={app.average_progress} className="h-1.5" />
        </div>
      )}

      {/* Final status — visible only once the client explicitly notified the candidate */}
      {app.final_status && (
        <div
          className={`mt-4 rounded-lg border px-3 py-3 text-sm ${FINAL_OUTCOME_TONE[app.final_status.outcome] || 'bg-muted border-border'}`}
          data-testid="application-final-status"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span className="font-semibold uppercase tracking-wide" data-testid="application-final-outcome">
              {(FINAL_OUTCOME_LABEL[app.final_status.outcome] || {})[isES ? 'es' : 'en'] || app.final_status.outcome}
            </span>
            {app.final_status.notified_at && (
              <span className="ml-auto text-xs opacity-70">{fmt(app.final_status.notified_at)}</span>
            )}
          </div>
          {app.final_status.message && (
            <p className="mt-2 whitespace-pre-wrap leading-relaxed text-foreground/80" data-testid="application-final-message">
              {app.final_status.message}
            </p>
          )}
        </div>
      )}

      {/* Report state hint — never shows scores, only that HR has it */}
      {!app.final_status && app.report?.state === 'shared_with_hr' && (
        <div
          className="mt-4 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-sm flex items-start gap-2"
          data-testid="application-report-shared"
        >
          <ShieldCheck className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" />
          <span className="text-foreground/85 leading-relaxed">
            {isES
              ? 'Tu informe ha sido compartido con el equipo de Recursos Humanos. Ellos se pondrán en contacto contigo.'
              : 'Your report has been shared with the HR team. They will be in touch with you.'}
          </span>
        </div>
      )}
      {app.report?.state === 'in_review' && (
        <div
          className="mt-4 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-sm flex items-start gap-2"
          data-testid="application-report-in-review"
        >
          <Clock className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
          <span className="text-foreground/85 leading-relaxed">
            {isES
              ? 'Tu evaluación fue enviada y sus resultados automáticos fueron procesados correctamente. Si tu proceso incluye un informe interpretativo profesional, este se elabora por separado.'
              : 'Your evaluation was submitted and its automatic results were processed successfully. If your process includes a professional interpretive report, it is prepared separately.'}
          </span>
        </div>
      )}

      {/* Toggle: assessments list */}
      {app.assignments?.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            data-testid="application-toggle-assessments"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded
              ? (isES ? 'Ocultar evaluaciones' : 'Hide assessments')
              : (isES ? `Ver evaluaciones (${app.assignments.length})` : `View assessments (${app.assignments.length})`)}
          </button>
          {expanded && (
            <ul className="mt-3 space-y-2" data-testid="application-assessments-list">
              {app.assignments.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20"
                  data-testid={`application-assessment-${a.id}`}
                >
                  <Inbox className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{localizeAssignmentTitle(a.title, i18n.language) || a.suite?.name || a.suite_code}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                      <span className="tabular-nums">{a.progress || 0}%</span>
                      {a.deadline && (
                        <>
                          <span className="text-muted-foreground/50">·</span>
                          <span>{isES ? 'Vence' : 'Due'} {fmt(a.deadline)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={ASSIGNMENT_TONE[a.status] || ASSIGNMENT_TONE.requested}
                  >
                    {a.status === 'requested'   ? (isES ? 'Pendiente'   : 'Pending')
                      : a.status === 'in_progress' ? (isES ? 'En curso' : 'In progress')
                      : a.status === 'submitted'   ? (isES ? 'Enviada'  : 'Submitted')
                      : a.status}
                  </Badge>
                  {a.status !== 'submitted' && app.application_status !== 'closed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/me/assignments/${a.id}`)}
                      data-testid={`application-open-assessment-${a.id}`}
                      className="gap-1.5"
                    >
                      {a.status === 'in_progress'
                        ? (isES ? 'Continuar' : 'Continue')
                        : (isES ? 'Iniciar' : 'Start')}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Primary next action — suppressed once a final outcome was communicated */}
      {nextLabel && !app.final_status && (
        <div className="mt-5 pt-4 border-t border-border/60 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm text-muted-foreground">
            {isES ? 'Próximo paso' : 'Next step'}
          </span>
          {['awaiting_hr_outcome', 'process_concluded'].includes(app.next_action?.kind) ? (
            <Badge variant="outline" className="gap-1.5">
              {app.next_action?.kind === 'process_concluded'
                ? <CheckCircle2 className="h-3 w-3" />
                : <Clock className="h-3 w-3" />}
              {nextLabel}
            </Badge>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-gradient-primary hover:opacity-90 gap-1.5"
              data-testid="application-next-action"
            >
              {nextLabel}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="font-medium truncate">{value}</div>
      </div>
    </div>
  );
}
